﻿import React from 'react'
import { createPortal } from 'react-dom'
import { Text, LoadingSpinner, SortableTableHeader } from '../../components/ui'
import { AppointmentRecordModal } from '../../components/centralAmparo/AppointmentRecordModal'
import { useUserLogs } from '../../hooks/useUserLogs'
import { supportCenterReportsService } from '../../services/supportCenterReports'
import type {
  SupportCenterAppointmentRecord,
  SupportCenterDashboardFilters,
  SupportCenterHistoryItem,
} from '../../types/supportCenterReports'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import { TRIAGE_STATUS_LABELS } from '../../types/supportCenterTriage'
import { triageColumnBadgeClass } from '../../components/centralAmparo/triagemHelpers'
import {
  appointmentStatusBadgeClass,
  formatCpf,
  formatDateTime,
  formatPhone,
  getErrorMessage,
  patientKinshipLabel,
} from '../../components/centralAmparo/dashboardHelpers'

const ITEMS_PER_PAGE = 20

type HistoricoSortKey =
  | 'scheduled_at'
  | 'holder_name'
  | 'patient_name'
  | 'attendant_name'
  | 'specialization_name'
  | 'status'
  | 'triage_status'
  | 'contact'

function compareSortValues(a: string, b: string, order: 'asc' | 'desc'): number {
  const cmp = a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' })
  return order === 'asc' ? cmp : -cmp
}

function sortValueForRow(row: SupportCenterHistoryItem, key: HistoricoSortKey): string {
  switch (key) {
    case 'scheduled_at':
      return row.scheduled_at || ''
    case 'holder_name':
      return (row.holder_name || '').trim()
    case 'patient_name':
      return (row.patient_name || '').trim()
    case 'attendant_name':
      return (row.attendant_name || '').trim()
    case 'specialization_name':
      return (row.specialization_name || '').trim()
    case 'status':
      return APPOINTMENT_STATUS_LABELS[row.status] || row.status
    case 'triage_status':
      return row.triage_status ? TRIAGE_STATUS_LABELS[row.triage_status] : 'Sem triagem'
    case 'contact':
      return `${row.patient_phone || ''} ${row.client_email || ''}`.trim()
  }
}

type DashboardHistoricoTabProps = {
  filters: SupportCenterDashboardFilters
  refreshToken: number
  active: boolean
  onLoadingChange: (loading: boolean) => void
}

const DashboardHistoricoTab: React.FC<DashboardHistoricoTabProps> = ({
  filters,
  refreshToken,
  active,
  onLoadingChange,
}) => {
  const { logView } = useUserLogs()

  const [rows, setRows] = React.useState<SupportCenterHistoryItem[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [sortBy, setSortBy] = React.useState<HistoricoSortKey>('scheduled_at')
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc')
  const [currentPage, setCurrentPage] = React.useState(1)

  const [detailOpen, setDetailOpen] = React.useState(false)
  const [selectedRecord, setSelectedRecord] =
    React.useState<SupportCenterAppointmentRecord | null>(null)
  const [recordLoading, setRecordLoading] = React.useState(false)
  const [recordError, setRecordError] = React.useState<string | null>(null)

  const loadHistory = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    setRecordError(null)
    try {
      const data = await supportCenterReportsService.listAppointments({
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        specialization_ids: filters.specializationIds.length ? filters.specializationIds : undefined,
        professional_user_ids: filters.professionalUserIds.length
          ? filters.professionalUserIds
          : undefined,
        attendant_email: filters.attendantEmail?.trim() || undefined,
        appointment_statuses: filters.appointmentStatuses.length
          ? filters.appointmentStatuses
          : undefined,
        triage_statuses: filters.triageStatuses.length ? filters.triageStatuses : undefined,
        channels: filters.channels.length ? filters.channels : undefined,
        patient_query: filters.patientQuery.trim() || undefined,
        policy_numbers: filters.policyNumbers.length ? filters.policyNumbers : undefined,
        convenio_codes: filters.convenioCodes.length ? filters.convenioCodes : undefined,
        assistance_ids: filters.assistanceIds.length ? filters.assistanceIds : undefined,
        referral_origin_ids: filters.referralOriginIds.length
          ? filters.referralOriginIds
          : undefined,
        demand_type_ids: filters.demandTypeIds.length ? filters.demandTypeIds : undefined,
        kinships: filters.kinships.length ? filters.kinships : undefined,
        broker_ids: filters.brokerIds.length ? filters.brokerIds : undefined,
        client_ids: filters.clientIds.length ? filters.clientIds : undefined,
      })
      setRows(data)
    } catch (e) {
      setRows([])
      setError(getErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.specializationIds,
    filters.professionalUserIds,
    filters.attendantEmail,
    filters.appointmentStatuses,
    filters.triageStatuses,
    filters.channels,
    filters.patientQuery,
    filters.policyNumbers,
    filters.convenioCodes,
    filters.assistanceIds,
    filters.referralOriginIds,
    filters.demandTypeIds,
    filters.kinships,
    filters.brokerIds,
    filters.clientIds,
  ])

  React.useEffect(() => {
    void loadHistory()
  }, [loadHistory, refreshToken])

  React.useEffect(() => {
    if (active) onLoadingChange(loading)
  }, [active, loading, onLoadingChange])

  const toggleSort = (key: string) => {
    const nextKey = key as HistoricoSortKey
    if (sortBy === nextKey) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(nextKey)
      setSortOrder(nextKey === 'scheduled_at' ? 'desc' : 'asc')
    }
    setCurrentPage(1)
  }

  const sortedRows = React.useMemo(() => {
    return [...rows].sort((a, b) =>
      compareSortValues(sortValueForRow(a, sortBy), sortValueForRow(b, sortBy), sortOrder)
    )
  }, [rows, sortBy, sortOrder])

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / ITEMS_PER_PAGE))
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedRows = sortedRows.slice(startIndex, endIndex)

  React.useEffect(() => {
    setCurrentPage(1)
  }, [rows])

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  const openRowDetail = async (row: SupportCenterHistoryItem) => {
    setRecordError(null)
    setRecordLoading(true)
    try {
      const record = await supportCenterReportsService.getAppointmentRecord(row.appointment_id)
      if (!record) {
        setSelectedRecord(null)
        setRecordError('Agendamento não encontrado.')
        return
      }
      setSelectedRecord(record)
      setDetailOpen(true)
      void logView('support_center_appointment', row.appointment_id, 'Visualizou agendamento no histórico do dashboard')
    } catch (e) {
      setSelectedRecord(null)
      setRecordError(getErrorMessage(e))
    } finally {
      setRecordLoading(false)
    }
  }

  const closeDetail = () => {
    if (recordLoading) return
    setDetailOpen(false)
    setSelectedRecord(null)
    setRecordError(null)
  }

  if (!active) return null

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{error}</Text>
        </div>
      )}

      {recordError && !detailOpen && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <Text size="sm">{recordError}</Text>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <Text size="lg" className="text-gray-900 dark:text-white">
            Lista de atendimentos
          </Text>
          <Text size="sm" color="muted">
            {rows.length} {rows.length === 1 ? 'registro' : 'registros'}
          </Text>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner />
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <Text size="sm" color="muted">
              Nenhum atendimento encontrado com os filtros selecionados.
            </Text>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <SortableTableHeader
                    sortKey="scheduled_at"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Data
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="holder_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Segurado
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="patient_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Paciente
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="attendant_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Profissional
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="specialization_name"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Especialidade
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="status"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Status
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="triage_status"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Triagem
                  </SortableTableHeader>
                  <SortableTableHeader
                    sortKey="contact"
                    currentSort={sortBy}
                    sortOrder={sortOrder}
                    onSort={toggleSort}
                    className="px-4 py-3"
                    compact
                  >
                    Contato
                  </SortableTableHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {paginatedRows.map((row) => (
                  <tr
                    key={row.appointment_id}
                    onClick={() => void openRowDetail(row)}
                    className="hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer"
                    title="Ver todos os campos do agendamento"
                  >
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200 whitespace-nowrap">
                      {formatDateTime(row.scheduled_at)
                        .split(',')
                        .map((part) => part.trim())
                        .filter(Boolean)
                        .map((part, index) => (
                          <div
                            key={`${row.appointment_id}-dt-${index}`}
                            className={
                              index === 0 ? undefined : 'text-xs text-gray-500 dark:text-gray-400'
                            }
                          >
                            {part}
                          </div>
                        ))}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      <div>{row.holder_name || '?'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {formatCpf(row.holder_cpf)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      <div>{row.patient_name || '?'}</div>
                      {patientKinshipLabel(row) ? (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {patientKinshipLabel(row)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      {row.attendant_name || '?'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      {row.specialization_name || '?'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${appointmentStatusBadgeClass(
                          row.status
                        )}`}
                      >
                        {APPOINTMENT_STATUS_LABELS[row.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${triageColumnBadgeClass(
                          row.triage_status
                        )}`}
                      >
                        {row.triage_status ? TRIAGE_STATUS_LABELS[row.triage_status] : 'Sem triagem'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                      <div>{formatPhone(row.patient_phone) || '?'}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {row.client_email || '?'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <Text size="sm" className="text-gray-700 dark:text-gray-300">
                Mostrando {startIndex + 1} a {Math.min(endIndex, sortedRows.length)} de{' '}
                {sortedRows.length} registros
              </Text>
              {totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Anterior
                  </button>
                  <div className="flex space-x-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                      let pageNumber: number
                      if (totalPages <= 5) {
                        pageNumber = index + 1
                      } else if (currentPage <= 3) {
                        pageNumber = index + 1
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + index
                      } else {
                        pageNumber = currentPage - 2 + index
                      }
                      return (
                        <button
                          key={pageNumber}
                          type="button"
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === pageNumber
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      )
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Próximo
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {recordLoading &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <LoadingSpinner />
          </div>,
          document.body
        )}

      {detailOpen && selectedRecord && (
        <AppointmentRecordModal record={selectedRecord} onClose={closeDetail} />
      )}
    </div>
  )
}

export default DashboardHistoricoTab
